import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Gift, HandHeart, Sparkles, TrendingUp, Heart, Globe2, HelpCircle, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import {
  decideRobots, buildCanonical, isCausePriority, isCityPriority,
  buildPageMeta, MIN_OFFERS_CITY, slugifyTaxonomy,
} from "@/lib/seoIndex";
import OfferCard from "@/components/OfferCard";
import Breadcrumbs from "@/components/Breadcrumbs";
import {
  getCauseEditorial, CAUSE_FAQ, CAUSE_WAYS_TO_HELP, PRIORITY_CAUSES,
} from "@/data/causeEditorial";

const CATEGORY_ICON = {
  donation: Gift, volunteering: HandHeart, pro_bono_service: Sparkles,
  impact_investment: TrendingUp, fundraising: Heart,
};

const SUPPORTED_LANGS = ["en", "de", "fr", "es"];

/**
 * Renders cause/country/category landing pages. The `kind` prop selects the
 * axis. Designed for max SEO yield: H1, descriptive intro, breadcrumbs,
 * category/cause breakdown, top offers, sibling links, FAQ schema.
 */
export default function HubLandingPage({ kind }) {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [citiesIndex, setCitiesIndex] = useState(null); // [{city, slug, count}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /* eslint-disable */
  useEffect(() => {
    setLoading(true);
    setError(false);
    const endpoint = { cause: "causes", country: "countries", category: "categories" }[kind];
    api.get(`/${endpoint}/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [kind, slug]);

  // Cities lookup — needed on cause pages to filter "active cities" to
  // hub pages that are actually indexable. Fetched once per session.
  useEffect(() => {
    if (kind !== "cause" || citiesIndex) return;
    api.get("/cities").then((r) => setCitiesIndex(r.data || [])).catch(() => setCitiesIndex([]));
  }, [kind, citiesIndex]);
  /* eslint-enable */

  // Build local variables for SEO
  const baseUrl = buildCanonical(`/${kind === "cause" ? "cause" : kind === "country" ? "country" : "category"}/${slug || ""}`);
  const apexName = data?.[kind === "cause" ? "cause" : kind === "country" ? "country" : "category"];
  const niceName = apexName
    ? (kind === "cause"
        ? t(`causes.${apexName}`, { defaultValue: apexName })
        : kind === "country"
        ? t(`countries.${apexName}`, { defaultValue: apexName })
        : t(`categories.${apexName}`, { defaultValue: apexName.replace(/_/g, " ") }))
    : (slug || "").replace(/-/g, " ");

  const seoTitleKey = `hub.${kind}.seo_title`;
  const seoDescKey = `hub.${kind}.seo_description`;
  const h1Key = `hub.${kind}.h1`;
  const introKey = `hub.${kind}.intro`;

  const hubMeta = buildPageMeta(kind, {
    causeName: kind === "cause" ? niceName : undefined,
    countryName: kind === "country" ? niceName : undefined,
    categoryName: kind === "category" ? niceName : undefined,
    totalOffers: data?.total_offers,
  });

  // Cause-editorial helpers — only computed for cause kind.
  const isPriorityCause = kind === "cause" && isCausePriority(slug);
  const causeEditorial = isPriorityCause ? getCauseEditorial(slug) : null;
  const localizedCauseFaqs = isPriorityCause
    ? CAUSE_FAQ.map((f) => ({
        q: f.q.replace(/\{\{cause\}\}/g, niceName || ""),
        a: f.a.replace(/\{\{cause\}\}/g, niceName || ""),
      }))
    : [];

  // Build a combined JSON-LD graph: keep CollectionPage + ItemList, and
  // append FAQPage when the priority cause FAQ is rendered. Single <script>.
  const jsonLdGraph = data
    ? (() => {
        const graph = [
          {
            "@type": "CollectionPage",
            name: `${niceName} — doing-good.org`,
            description: hubMeta.description,
            url: baseUrl,
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: data.total_offers,
              itemListElement: (data.top_offers || []).map((o, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "Organization",
                  name: o.organization,
                  url: o.url,
                  description: (o.description || "").slice(0, 200),
                },
              })),
            },
          },
        ];
        if (localizedCauseFaqs.length > 0) {
          graph.push({
            "@type": "FAQPage",
            mainEntity: localizedCauseFaqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          });
        }
        return { "@context": "https://schema.org", "@graph": graph };
      })()
    : undefined;

  useSEO({
    robots: decideRobots({
      pageType: kind,
      totalOffers: data?.total_offers,
      isPriority: kind === "cause" && isCausePriority(slug),
    }),
    title: hubMeta.title,
    description: hubMeta.description,
    canonical: baseUrl,
    alternates: SUPPORTED_LANGS.map((l) => ({ hreflang: l, href: `${baseUrl}?lang=${l}` }))
      .concat([{ hreflang: "x-default", href: baseUrl }]),
    jsonLd: jsonLdGraph,
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="h-10 w-2/3 bg-[#EFEAE0] rounded animate-pulse" />
        <div className="mt-6 h-4 w-1/2 bg-[#EFEAE0] rounded animate-pulse" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="hub-not-found">
        <p className="text-3xl mb-2">🔍</p>
        <h1 className="text-3xl font-black">{t("city.not_found_title", { defaultValue: "Not found" })}</h1>
        <Link to="/offers" className="btn-primary mt-6 inline-flex" data-testid="cta-browse-all">
          {t("city.browse_all", { defaultValue: "Browse all offers" })}
        </Link>
      </div>
    );
  }

  // Breadcrumb path varies by kind
  const breadcrumbs = (() => {
    if (kind === "cause") return [{ label: t("nav.causes", { defaultValue: "Causes" }), to: "/causes" }, { label: niceName }];
    if (kind === "country") return [{ label: t("nav.countries", { defaultValue: "Countries" }), to: "/countries" }, { label: niceName }];
    return [{ label: t("nav.categories", { defaultValue: "How to help" }), to: "/categories" }, { label: niceName }];
  })();

  // Filter param key in /offers route
  const offerParamKey = kind === "cause" ? "cause_area" : kind === "country" ? "country" : "category";

  // Related causes — for priority causes, keep only siblings that are
  // themselves indexable. For non-priority causes the existing behavior
  // (show all siblings) is preserved.
  const relatedCauses = (() => {
    if (kind !== "cause") return data.sibling_causes || [];
    const all = data.sibling_causes || [];
    return all
      .filter((s) => s.count >= 10 || isCausePriority(s.slug))
      .slice(0, 8);
  })();

  // Sibling list: causes for cause page (indexability-filtered), cities for country, categories for category.
  // For country: keep only cities with at least 3 active offers and limit
  // to a scannable top-8 so we never ship a giant link list.
  const siblings =
    kind === "cause" ? relatedCauses :
    kind === "country" ? (data.cities || []).filter((c) => (c.count || 0) >= 3).slice(0, 8) :
    (data.sibling_categories || []);

  // Render config for the "sibling card" — labels & link paths differ
  const renderSibling = (s) => {
    if (kind === "cause") {
      return { to: `/cause/${s.slug}`, label: t(`causes.${s.cause}`, { defaultValue: s.cause }), count: s.count };
    }
    if (kind === "country") {
      const cityLabel = t(`cities.${s.city}`, { defaultValue: s.city });
      return {
        to: `/c/${s.slug}`,
        // Descriptive anchor "Volunteer opportunities in {city}" — better
        // for search & users than bare city names.
        label: t("country.city_anchor", {
          defaultValue: "Volunteer opportunities in {{city}}",
          city: cityLabel,
        }),
        count: s.count,
      };
    }
    return { to: `/category/${s.slug}`, label: t(`categories.${s.category}`, { defaultValue: s.category }), count: s.count };
  };

  const categoryEntries = Object.entries(data.by_category || {}).sort(([, a], [, b]) => b - a);
  const causeEntries = Object.entries(data.by_cause || {}).sort(([, a], [, b]) => b - a);

  // ---- Cause page: derived sections --------------------------------------
  // Popular city × cause combos for this cause — only cities where the
  // City×Cause page itself meets the >=3 active-offers bar so we never
  // promote empty combo pages from this hub.
  const popularCityCombosForCause = (() => {
    if (kind !== "cause" || !data.by_city || !citiesIndex) return [];
    const lookup = new Map(citiesIndex.map((c) => [c.city, c]));
    return Object.entries(data.by_city)
      .map(([cityName, count]) => {
        const c = lookup.get(cityName);
        if (!c) return null;
        if ((count || 0) < 3) return null;
        return { city: cityName, slug: c.slug, count };
      })
      .filter(Boolean)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  })();

  // Active cities for this cause — only cities whose own hub is indexable
  // (≥ MIN_OFFERS_CITY OR priority slug). Ordered by cause-specific count.
  const activeCitiesForCause = (() => {
    if (kind !== "cause" || !data.by_city || !citiesIndex) return [];
    const lookup = new Map(citiesIndex.map((c) => [c.city, c]));
    return Object.entries(data.by_city)
      .map(([cityName, count]) => {
        const c = lookup.get(cityName);
        if (!c) return null;
        const indexable = c.count >= MIN_OFFERS_CITY || isCityPriority(c.slug);
        if (!indexable) return null;
        return { city: cityName, slug: c.slug, count, country: c.country };
      })
      .filter(Boolean)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  })();

  return (
    <div data-testid={`hub-page-${kind}-${slug}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#F5F1E6] via-[#EFEAE0] to-[#E8EDDF]" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 lg:py-20">
          <Breadcrumbs crumbs={breadcrumbs} />
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" data-testid="hub-h1">
            {kind === "cause"
              ? t("cause.h1", {
                  defaultValue: "Support {{name}} organisations across Europe.",
                  name: niceName,
                })
              : kind === "country"
              ? t("country.h1", {
                  defaultValue: "Volunteer and donate in {{name}}",
                  name: niceName,
                })
              : t(h1Key, { defaultValue: niceName, name: niceName })}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#3A463C] max-w-2xl">
            {kind === "cause"
              ? t("cause.subheadline", {
                  defaultValue:
                    "Find volunteering, donation and professional support opportunities connected to {{name}}.",
                  name: niceName,
                })
              : t(introKey, {
                  defaultValue:
                    "{{count}} active pro bono opportunities for {{name}} across Europe — local NGOs you can donate to, volunteer with or invest in.",
                  count: data.total_offers,
                  name: niceName,
                })}
          </p>
          <div className="mt-8 inline-flex flex-wrap gap-3">
            <span className="px-4 py-2 rounded-full bg-white border border-[#DED7C7] text-sm">
              <strong className="text-[#5B8C66] font-black">{data.total_offers}</strong>{" "}
              {t("city.stat_offers", { defaultValue: "offers" })}
            </span>
            <span className="px-4 py-2 rounded-full bg-white border border-[#DED7C7] text-sm">
              <strong className="text-[#5B8C66] font-black">{data.organizations}</strong>{" "}
              {t("city.stat_orgs", { defaultValue: "organisations" })}
            </span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/offers?${offerParamKey}=${encodeURIComponent(apexName)}`} className="btn-primary" data-testid="hub-cta-see-all">
              {kind === "cause"
                ? t("cause.cta_explore", { defaultValue: "Explore opportunities" })
                : t("hub.cta_see_all", { defaultValue: "See all {{name}} offers", name: niceName })}
              <ArrowUpRight size={16} className="ml-1.5" strokeWidth={2} />
            </Link>
            {kind === "cause" ? (
              <Link
                to="/cities"
                className="btn-pill border border-[#1F2A24] px-5 py-3 text-sm font-semibold inline-flex items-center bg-white hover:bg-[#F5F1E6]"
                data-testid="hub-cta-browse-cities"
              >
                {t("cause.cta_browse_cities", { defaultValue: "Browse cities" })}
              </Link>
            ) : (
              <Link to="/list-offer" className="btn-pill border border-[#1F2A24] px-5 py-3 text-sm font-semibold inline-flex items-center bg-white hover:bg-[#F5F1E6]" data-testid="hub-cta-submit">
                {t("city.cta_submit", { defaultValue: "Submit your own" })}
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Top offers — placed immediately after the hero so visitors see
          real opportunities first, in line with the City×Cause page. */}
      {data.top_offers?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
            <div>
              <p className="eyebrow">{t("city.section_top_eyebrow", { defaultValue: "Featured" })}</p>
              <h2 className="mt-2 text-2xl md:text-3xl font-black">
                {t("hub.section_top_title", { defaultValue: "Top opportunities — {{name}}", name: niceName })}
              </h2>
            </div>
            <Link to={`/offers?${offerParamKey}=${encodeURIComponent(apexName)}`} className="text-sm font-bold text-[#5B8C66] inline-flex items-center gap-1">
              {t("city.see_all_link", { defaultValue: "See all {{count}}", count: data.total_offers })}
              <ArrowUpRight size={14} strokeWidth={2.5} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="hub-top-offers">
            {data.top_offers.map((o) => <OfferCard key={o.id} offer={o} />)}
          </div>
        </section>
      )}

      {/* Ways to help this cause — priority causes only. Maps to existing
          /offers filters so each card lands on a useful filtered list. */}
      {isPriorityCause && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10" data-testid="cause-ways-to-help">
          <p className="eyebrow">
            {t("cause.section_ways_eyebrow", { defaultValue: "Ways to help" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("cause.section_ways_title", {
              defaultValue: "Four practical ways to support {{name}}",
              name: niceName,
            })}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAUSE_WAYS_TO_HELP.map((w) => {
              const Icon = CATEGORY_ICON[w.category] || Sparkles;
              const inCat = (data.by_category || {})[w.category] || 0;
              return (
                <Link
                  key={w.key}
                  to={`/offers?cause_area=${encodeURIComponent(apexName)}&category=${w.category}`}
                  className="card-clay p-5 hover:shadow-md transition group block"
                  data-testid={`cause-way-${w.key}-link`}
                >
                  <Icon size={20} strokeWidth={1.75} className="text-[#5B8C66]" />
                  <p className="mt-3 text-sm font-bold leading-snug">
                    {t(`cause.ways.${w.key}.label`, { defaultValue: w.label })}
                  </p>
                  <p className="mt-1.5 text-xs text-[#5F6B61] leading-relaxed">
                    {t(`cause.ways.${w.key}.body`, { defaultValue: w.body })}
                  </p>
                  {inCat > 0 && (
                    <p className="mt-3 text-xs text-[#5B8C66] font-semibold inline-flex items-center gap-1">
                      {inCat} {inCat === 1
                        ? t("city.offer_singular", { defaultValue: "offer" })
                        : t("city.offer_plural", { defaultValue: "offers" })}
                      <ArrowUpRight size={12} strokeWidth={2.5} />
                    </p>
                  )}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Category breakdown (always relevant) */}
      {categoryEntries.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">
          <p className="eyebrow">{t("hub.section_categories", { defaultValue: "How to help" })}</p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("hub.section_categories_title", { defaultValue: "Pick your path" })}
          </h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categoryEntries.map(([cat, n]) => {
              const Icon = CATEGORY_ICON[cat] || Sparkles;
              return (
                <Link
                  key={cat}
                  to={`/offers?${offerParamKey}=${encodeURIComponent(apexName)}&category=${cat}`}
                  className="card-clay p-5 hover:shadow-md transition group"
                  data-testid={`hub-category-${cat}`}
                >
                  <Icon size={20} strokeWidth={1.75} className="text-[#5B8C66]" />
                  <p className="mt-3 text-sm font-bold leading-snug">
                    {t(`categories.${cat}`, { defaultValue: cat.replace(/_/g, " ") })}
                  </p>
                  <p className="mt-1 text-xs text-[#5F6B61]">
                    {n} {n === 1 ? t("city.offer_singular", { defaultValue: "offer" }) : t("city.offer_plural", { defaultValue: "offers" })}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* About / editorial intro — priority causes only. Placed AFTER
          opportunities and How-to-Help so the longer explanatory text
          never pushes real offers below the fold. */}
      {causeEditorial?.intro?.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="cause-editorial-intro">
          <p className="eyebrow">
            {t("cause.section_intro_eyebrow", { defaultValue: "About this cause" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("cause.section_intro_title", {
              defaultValue: "{{name}} in Europe — and how you can help",
              name: niceName,
            })}
          </h2>
          <div className="prose mt-6 max-w-none text-[#3A463C] leading-relaxed space-y-4 text-base md:text-lg">
            {causeEditorial.intro.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </section>
      )}

      {/* Cause breakdown only meaningful on country/category pages */}
      {kind !== "cause" && causeEntries.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">
          <p className="eyebrow">{t("hub.section_causes", { defaultValue: "Causes" })}</p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("hub.section_causes_title", { defaultValue: "{{name}} — by cause", name: niceName })}
          </h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {causeEntries.map(([cause, n]) => (
              <Link
                key={cause}
                to={`/offers?${offerParamKey}=${encodeURIComponent(apexName)}&cause_area=${encodeURIComponent(cause)}`}
                className="px-4 py-2 rounded-full border border-[#DED7C7] bg-white text-sm hover:border-[#5B8C66] hover:text-[#5B8C66] transition"
              >
                {t(`causes.${cause}`, { defaultValue: cause })}
                <span className="ml-1.5 text-xs text-[#5F6B61]">· {n}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Siblings */}
      {siblings.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12 mb-8">
          <p className="eyebrow flex items-center gap-1.5">
            <Globe2 size={12} strokeWidth={2.5} />
            {t(kind === "country" ? "country.cities_in_country" : "hub.section_siblings", {
              defaultValue: kind === "country" ? "Cities in {{name}}" : "Related",
              name: niceName,
            })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t(
              kind === "country"
                ? "country.cities_in_country_title"
                : kind === "cause"
                ? "cause.related_causes_title"
                : "category.related_title",
              {
                defaultValue:
                  kind === "country" ? "Top volunteering cities in {{name}}" :
                  kind === "cause" ? "Other causes" :
                  "Other ways to help",
                name: niceName,
              }
            )}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {siblings.map((s) => {
              const r = renderSibling(s);
              return (
                <Link
                  key={r.to}
                  to={r.to}
                  className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between"
                  data-testid={`hub-sibling-${r.to.split("/").pop()}`}
                >
                  <div>
                    <p className="font-bold">{r.label}</p>
                    <p className="text-xs text-[#5F6B61]">
                      {r.count} {r.count === 1
                        ? t("city.offer_singular", { defaultValue: "offer" })
                        : t("city.offer_plural", { defaultValue: "offers" })}
                    </p>
                  </div>
                  <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66]" />
                </Link>
              );
            })}
          </div>
        </section>
      )}
      {/* Active cities for this cause — priority causes only. Only links to
          city hubs that are themselves indexable (≥10 offers OR priority). */}
      {isPriorityCause && activeCitiesForCause.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="cause-active-cities">
          <p className="eyebrow flex items-center gap-1.5">
            <Globe2 size={12} strokeWidth={2.5} />
            {t("cause.section_cities_eyebrow", { defaultValue: "Active cities" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("cause.section_cities_title", {
              defaultValue: "Where {{name}} organisations are active",
              name: niceName,
            })}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {activeCitiesForCause.map((c) => (
              <Link
                key={c.slug}
                to={`/c/${c.slug}`}
                className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between"
                data-testid={`cause-city-${c.slug}`}
              >
                <div>
                  <p className="font-bold">{t(`cities.${c.city}`, { defaultValue: c.city })}</p>
                  <p className="text-xs text-[#5F6B61]">
                    {c.count} {c.count === 1
                      ? t("city.offer_singular", { defaultValue: "offer" })
                      : t("city.offer_plural", { defaultValue: "offers" })}
                  </p>
                </div>
                <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66]" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Popular city × cause combos — priority causes only. Each link is
          a descriptive anchor pointing at /c/{city}/{cause} so search
          engines and users get cause-specific landing pages. */}
      {isPriorityCause && popularCityCombosForCause.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="cause-popular-cities">
          <p className="eyebrow flex items-center gap-1.5">
            <Globe2 size={12} strokeWidth={2.5} />
            {t("cause.section_popular_cities_eyebrow", { defaultValue: "Discovery" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("cause.section_popular_cities_title", {
              defaultValue: "Popular cities for {{name}} opportunities",
              name: niceName,
            })}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {popularCityCombosForCause.map((c) => (
              <Link
                key={c.slug}
                to={`/c/${c.slug}/${slug}`}
                className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between gap-3"
                data-testid={`cause-popular-${c.slug}`}
              >
                <div className="min-w-0">
                  <p className="font-bold leading-snug">
                    {t("cause.popular_city_anchor", {
                      defaultValue: "{{cause}} opportunities in {{city}}",
                      cause: niceName,
                      city: t(`cities.${c.city}`, { defaultValue: c.city }),
                    })}
                  </p>
                  <p className="mt-1 text-xs text-[#5F6B61]">
                    {c.count} {c.count === 1
                      ? t("city.offer_singular", { defaultValue: "offer" })
                      : t("city.offer_plural", { defaultValue: "offers" })}
                  </p>
                </div>
                <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66] shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Cause-specific FAQs — priority causes only. The same 6 questions
          are rendered with the cause name interpolated so each page renders
          unique crawlable Q/A text (mirrored in the FAQPage JSON-LD above). */}
      {isPriorityCause && localizedCauseFaqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="cause-faqs">
          <p className="eyebrow flex items-center gap-1.5">
            <HelpCircle size={12} strokeWidth={2.5} />
            {t("cause.section_faq_eyebrow", { defaultValue: "FAQ" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("cause.section_faq_title", {
              defaultValue: "Common questions about supporting {{name}}",
              name: niceName,
            })}
          </h2>
          <div className="mt-6 divide-y divide-[#E5DECC] border-y border-[#E5DECC]">
            {localizedCauseFaqs.map((f, i) => (
              <details
                key={i}
                className="group py-5"
                data-testid={`cause-faq-${i}`}
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-bold text-[#1F2A24] leading-snug">{f.q}</span>
                  <span className="mt-1 text-[#5B8C66] group-open:rotate-45 transition-transform select-none">+</span>
                </summary>
                <p className="mt-3 text-[#3A463C] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA — priority causes only. Calm, civic, action-oriented. */}
      {isPriorityCause && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16" data-testid="cause-final-cta">
          <div className="card-clay p-10 md:p-14 text-center bg-gradient-to-br from-[#5B8C66] to-[#3F6147] text-white">
            <Users size={28} strokeWidth={1.5} className="mx-auto opacity-80" />
            <h2 className="mt-4 text-3xl md:text-4xl font-black">
              {t("cause.final_cta_title", {
                defaultValue: "Ready to support {{name}}?",
                name: niceName,
              })}
            </h2>
            <p className="mt-3 text-white/80 max-w-xl mx-auto">
              {t("cause.final_cta_body", {
                defaultValue:
                  "Browse active opportunities and contact organisations directly. We don't process donations or sit between you and them.",
              })}
            </p>
            <div className="mt-6 inline-flex flex-wrap gap-3 justify-center">
              <Link
                to={`/offers?cause_area=${encodeURIComponent(apexName)}`}
                className="btn-pill bg-white text-[#1F2A24] px-6 py-3 text-sm font-bold inline-flex items-center hover:bg-[#F5F1E6]"
                data-testid="cause-final-cta-explore"
              >
                {t("cause.cta_explore", { defaultValue: "Explore opportunities" })}
                <ArrowUpRight size={14} className="ml-1" strokeWidth={2.5} />
              </Link>
              <Link
                to="/list-offer"
                className="btn-pill border border-white/40 px-6 py-3 text-sm font-bold inline-flex items-center hover:bg-white/10"
                data-testid="cause-final-cta-list"
              >
                {t("cause.cta_list_offer", { defaultValue: "For organisations" })}
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
